import type { Rule } from "eslint"

import type { ASTNode } from "../types/ast"

/** Array-prototype methods whose call expression always returns an array. */
const ARRAY_PRODUCING_METHODS: ReadonlySet<string> = new Set(["map", "filter", "slice", "concat", "split"])

const rule: Rule.RuleModule = {
  meta: {
    type: "suggestion",
    docs: {
      description: "Prefer .flatMap() over .map().flat() and nested transformations",
      recommended: true,
    },
    fixable: "code",
    schema: [
      {
        type: "object",
        properties: {
          checkNestedMaps: {
            type: "boolean",
            default: true,
          },
        },
        additionalProperties: false,
      },
    ],
    messages: {
      preferFlatMapOverMapFlat: "Use .flatMap() instead of .map().flat()",
      preferFlatMapNested: "Consider .flatMap() for nested transformations that return arrays",
      preferFlatMapChain: "Use .flatMap() instead of chained .map() operations that flatten results",
    },
  },

  create(context) {
    const options = context.options[0] || {}
    const checkNestedMaps = options.checkNestedMaps !== false

    function isMapFollowedByFlat(node: ASTNode): boolean {
      if (node.type !== "CallExpression") return false

      const callee = node.callee
      if (callee.type !== "MemberExpression") return false

      // Check if this is .flat()
      if (callee.property.name === "flat") {
        const object = callee.object

        // Check if the object is a .map() call
        if (
          object.type === "CallExpression" &&
          object.callee.type === "MemberExpression" &&
          object.callee.property.name === "map"
        ) {
          return true
        }
      }

      return false
    }

    /**
     * Heuristic for array literals that look like flatten candidates.
     * Skips append/identity patterns (`[...arr, x]`, `[...arr]`) and tuple
     * shapes (`[k, v]`) — those are not nested arrays, so .flatMap()
     * doesn't apply. Only flag when at least one element is itself an
     * ArrayExpression, signaling a true nested-array shape.
     */
    function isFlattenCandidateArrayLiteral(arrayExpr: ASTNode): boolean {
      const elements = arrayExpr.elements ?? []
      if (elements.some((el: ASTNode | null) => el && el.type === "SpreadElement")) {
        return false
      }
      return elements.some((el: ASTNode | null) => el && el.type === "ArrayExpression")
    }

    function returnsArray(functionNode: ASTNode): boolean {
      if (!functionNode || !functionNode.body) return false

      // Arrow function with expression body
      if (functionNode.body.type === "ArrayExpression") {
        return isFlattenCandidateArrayLiteral(functionNode.body)
      }

      // Arrow function with call expression body
      if (functionNode.body.type === "CallExpression") {
        const call = functionNode.body
        if (call.callee.type === "MemberExpression") {
          const methodName = call.callee.property.name
          // Common methods that return arrays
          if (ARRAY_PRODUCING_METHODS.has(methodName)) {
            return true
          }
        }
      }

      // Function with block body — any return statement returning an array shape?
      if (functionNode.body.type === "BlockStatement") {
        return functionNode.body.body.some(returnsArrayShape)
      }

      return false
    }

    /** A single statement that's `return <array-shaped-expression>`. */
    function returnsArrayShape(stmt: ASTNode): boolean {
      if (stmt.type !== "ReturnStatement" || !stmt.argument) return false
      if (stmt.argument.type === "ArrayExpression") return isFlattenCandidateArrayLiteral(stmt.argument)
      if (
        stmt.argument.type === "CallExpression" &&
        stmt.argument.callee.type === "MemberExpression" &&
        ARRAY_PRODUCING_METHODS.has(stmt.argument.callee.property.name)
      ) {
        return true
      }
      return false
    }

    function isNestedMapReturningArrays(node: ASTNode): boolean {
      if (node.type !== "CallExpression") return false

      const callee = node.callee
      if (callee.type !== "MemberExpression") return false

      // Check if this is .map()
      if (callee.property.name === "map") {
        const callback = node.arguments[0]
        if (callback && (callback.type === "ArrowFunctionExpression" || callback.type === "FunctionExpression")) {
          return returnsArray(callback)
        }
      }

      return false
    }

    /**
     * Skip the warning when the receiver (`.map`'s left side) is a known
     * non-collection monad constructor call. The rule has no type info, so
     * it can't tell `arr.map(cb => arr2)` (collection, .flatMap is the fix)
     * from `Try(...).map(cb => arr)` (non-collection — .flatMap on Try
     * expects Try<T>, not an array, so .flatMap is NOT the fix).
     *
     * We can't enumerate every non-collection receiver, but we *can* detect
     * the unambiguous case: the receiver is literally `Try(...)` /
     * `Option(...)` / `Either(...)` / `Right(...)` / `Left(...)` / etc.
     * When that holds, suppress the warning. For bare identifiers (`items`)
     * or array literals or array-method chains, we can't tell, so the rule
     * still fires as before.
     */
    function isNonCollectionMonadReceiver(node: ASTNode): boolean {
      const monadConstructors = new Set([
        "Option",
        "Some",
        "None",
        "Either",
        "Right",
        "Left",
        "Try",
        "Success",
        "Failure",
        "Task",
        "Ok",
        "Err",
        "IO",
      ])
      // Bare constructor: Try(...), Option(...), Right(x), etc.
      if (
        node.type === "CallExpression" &&
        node.callee?.type === "Identifier" &&
        monadConstructors.has(node.callee.name)
      ) {
        return true
      }
      // Companion call: Either.right(x), Option.none(), Try.success(v)
      if (
        node.type === "CallExpression" &&
        node.callee?.type === "MemberExpression" &&
        node.callee.object?.type === "Identifier" &&
        monadConstructors.has(node.callee.object.name)
      ) {
        return true
      }
      return false
    }

    return {
      CallExpression(node: ASTNode) {
        // Check for .map().flat() pattern first (highest priority)
        if (isMapFollowedByFlat(node)) {
          const sourceCode = context.sourceCode

          context.report({
            node,
            messageId: "preferFlatMapOverMapFlat",
            fix(fixer) {
              // Get the .map() call
              const mapCall = (node.callee as ASTNode).object
              const mapCallText = sourceCode.getText(mapCall)

              // Replace .map() with .flatMap() and remove .flat()
              const flatMapText = mapCallText.replace(/\.map\s*\(/, ".flatMap(")

              return fixer.replaceText(node, flatMapText)
            },
          })
          return // Don't check other patterns if we found .map().flat()
        }

        // Check for chained maps where intermediate results are arrays (highest priority after map().flat())
        if (node.callee.type === "MemberExpression" && node.callee.property.name === "map") {
          const object = node.callee.object
          if (
            object.type === "CallExpression" &&
            object.callee.type === "MemberExpression" &&
            object.callee.property.name === "map"
          ) {
            // Check if the first map returns arrays
            const firstMapCallback = object.arguments[0]
            if (firstMapCallback && returnsArray(firstMapCallback)) {
              context.report({
                node: object, // Report on the first map call
                messageId: "preferFlatMapChain",
              })
              return // Don't check other patterns for this chain
            }
          }
        }

        // Check for nested maps that return arrays (but not if they're part of map().flat() or chains)
        if (checkNestedMaps && isNestedMapReturningArrays(node)) {
          // Don't flag if this map is immediately followed by flat()
          if (
            node.parent &&
            node.parent.type === "MemberExpression" &&
            node.parent.parent &&
            node.parent.parent.type === "CallExpression" &&
            node.parent.property.name === "flat"
          ) {
            return // Skip - this will be handled by the map().flat() rule
          }

          // Don't flag if this map is part of a chain (either as first or second map)
          const object = node.callee?.object
          if (
            object?.type === "CallExpression" &&
            object.callee?.type === "MemberExpression" &&
            object.callee?.property?.name === "map"
          ) {
            return // Skip - this is part of a chain
          }

          // Check if this map feeds into another map
          if (
            node.parent?.type === "MemberExpression" &&
            node.parent.parent?.type === "CallExpression" &&
            node.parent.parent.callee?.property?.name === "map"
          ) {
            return // Skip - this feeds into a chain
          }

          // Don't flag when the receiver is a known non-collection monad
          // constructor — .flatMap on Try / Option / Either / etc. expects
          // a wrapped value, not an array, so suggesting it is wrong.
          if (object && isNonCollectionMonadReceiver(object)) {
            return
          }

          context.report({
            node,
            messageId: "preferFlatMapNested",
          })
        }
      },
    }
  },
}

export default rule
